import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { SharePermission } from "@/types/share";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/components/auth/AuthProvider";
import { Loader2, UserX, Copy, Link as LinkIcon, ExternalLink } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from 'react-i18next';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface ShareDialogProps {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onShare?: () => void;
}

// Type for public link data
interface PublicLinkData {
  id: string;
  project_id: string;
  created_by: string;
  created_at: string;
  expires_at: string | null;
  active: boolean;
}

const validateEmail = (email: string): boolean => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};

export function ShareDialog({ projectId, open, onOpenChange, onShare }: ShareDialogProps) {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [permission, setPermission] = useState<SharePermission>("view");
  const [isLoading, setIsLoading] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [editingShareId, setEditingShareId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>("users");
  const [publicLinkCreated, setPublicLinkCreated] = useState(false);
  const [publicLinkToken, setPublicLinkToken] = useState<string | null>(null);
  const [publicLinkLoading, setPublicLinkLoading] = useState(false);
  const { user } = useAuth();
  const { t } = useTranslation();

  const { data: contacts } = useQuery({
    queryKey: ["crew-contacts", user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      const { data, error } = await supabase
        .from("crew_contacts")
        .select("*")
        .eq('user_id', user.id)
        .order("name");

      if (error) throw error;
      return data || [];
    },
    enabled: !!user,
  });

  const { data: shares, refetch: refetchShares } = useQuery({
    queryKey: ["project-shares", projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("project_shares")
        .select(`
          *,
          shared_with_profile:profiles!project_shares_shared_with_fkey(
            full_name,
            email
          )
        `)
        .eq("project_id", projectId);

      if (error) throw error;
      return data || [];
    },
    enabled: !!projectId,
  });

  const { data: publicShareStatus, refetch: refetchPublicShare } = useQuery({
    queryKey: ["project-public-share", projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projects')
        .select('public_share_token, is_public_shared, public_share_revoked, public_share_expiration, public_share_editable')
        .eq('id', projectId)
        .maybeSingle();

      if (error) throw error;
      
      if (data?.is_public_shared && !data?.public_share_revoked) {
        setPublicLinkCreated(true);
        setPublicLinkToken(data.public_share_token);
      }
      
      return data;
    },
    enabled: !!projectId && open,
  });

  const handleUpdatePermission = async (shareId: string, newPermission: SharePermission) => {
    try {
      const { error } = await supabase
        .from("project_shares")
        .update({ permission: newPermission })
        .eq("id", shareId);

      if (error) {
        toast.error(t('share.failed_update_permission'));
        return;
      }

      toast.success(t('share.permission_updated'));
      refetchShares();
    } catch (error) {
      console.error('Error updating permission:', error);
      toast.error(t('share.error_updating_permission'));
    }
  };

  const handleContactSelect = (contactId: string) => {
    const selectedContact = contacts?.find(contact => contact.id === contactId);
    if (selectedContact?.email) {
      setEmail(selectedContact.email);
      setEmailError("");
    } else {
      toast.error(t('share.contact_no_email'));
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    if (newEmail && !validateEmail(newEmail)) {
      setEmailError(t('share.invalid_email'));
    } else {
      setEmailError("");
    }
  };

  const handleRemoveShare = async (shareId: string) => {
    const { error } = await supabase
      .from("project_shares")
      .delete()
      .eq("id", shareId);

    if (error) {
      toast.error(t('share.failed_remove_share'));
      return;
    }

    toast.success(t('share.share_removed'));
    refetchShares();
  };

  const handleCreatePublicLink = async () => {
    if (!projectId || !user) return;

    setPublicLinkLoading(true);
    try {
      // If a link already exists, return it
      if (publicShareStatus?.is_public_shared && !publicShareStatus?.public_share_revoked) {
        setPublicLinkCreated(true);
        setPublicLinkToken(publicShareStatus.public_share_token);
        setPublicLinkLoading(false);
        return;
      }

      // Generate a new share token
      const shareToken = crypto.randomUUID();

      // Update the project with public sharing enabled
      const { error } = await supabase
        .from('projects')
        .update({
          public_share_token: shareToken,
          is_public_shared: true,
          public_share_revoked: false,
          public_share_expiration: null // No expiration
        })
        .eq('id', projectId);

      if (error) {
        console.error('Error creating public link:', error);
        toast.error("Failed to create public link");
        setPublicLinkLoading(false);
        return;
      }

      // Create a tracking record
      await supabase
        .from('public_project_shares')
        .insert({
          project_id: projectId,
          share_token: shareToken,
          access_count: 0
        });

      setPublicLinkCreated(true);
      setPublicLinkToken(shareToken);
      toast.success("Public link created successfully");
      refetchPublicShare();
    } catch (error) {
      console.error('Error creating public link:', error);
      toast.error("An error occurred while creating the public link");
    } finally {
      setPublicLinkLoading(false);
    }
  };

  const handleDisablePublicLink = async () => {
    if (!publicLinkToken) return;

    try {
      const { error } = await supabase
        .from('projects')
        .update({ 
          is_public_shared: false,
          public_share_revoked: true
        })
        .eq('id', projectId);

      if (error) {
        toast.error("Failed to disable public link");
        return;
      }

      setPublicLinkCreated(false);
      setPublicLinkToken(null);
      toast.success("Public link disabled");
      refetchPublicShare();
    } catch (error) {
      console.error('Error disabling public link:', error);
      toast.error("An error occurred while disabling the public link");
    }
  };

  const copyPublicLinkToClipboard = () => {
    if (!publicLinkToken) return;
    
    const url = `${window.location.origin}/public-project/${publicLinkToken}`;
    navigator.clipboard.writeText(url).then(() => {
      toast.success("Public link copied to clipboard");
    }).catch(() => {
      toast.error("Failed to copy link");
    });
  };

  const handleInviteUser = async () => {
    if (!email || !projectId || !user) return;
    if (!validateEmail(email)) {
      setEmailError(t('share.invalid_email'));
      return;
    }
    
    setIsLoading(true);
    try {
      const { data: invitationLink, error: inviteError } = await supabase
        .from('invitation_links')
        .insert({
          created_by: user.id,
          max_uses: 1,
          expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
          metadata: {
            projectId,
            permission,
            inviterEmail: user.email
          }
        })
        .select()
        .single();

      if (inviteError) throw inviteError;

      const { data, error: functionError } = await supabase.functions.invoke('send-invitation', {
        body: {
          email,
          invitationId: invitationLink.id,
          projectId,
          inviterName: user.email,
          permission
        }
      });

      if (functionError) {
        console.error('Function error:', functionError);
        if (functionError.message?.includes('rate limit') || functionError.code === 429) {
          toast.error(t('share.too_many_invitations'));
        } else if (functionError.message?.includes('invalid recipient email')) {
          toast.error(t('share.invalid_recipient_email'));
        } else {
          toast.error(t('share.failed_send_invitation', { error: functionError.message || t('share.unknown_error') }));
        }
        return;
      }

      toast.success(t('share.invitation_sent'));
      onOpenChange(false);
      setEmail("");
      setShowInviteDialog(false);
    } catch (error: any) {
      console.error('Error sending invitation:', error);
      toast.error(t('share.error_sending_invitation'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleShare = async () => {
    if (!email || !projectId || !user) return;
    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address");
      return;
    }
    
    setIsLoading(true);
    try {
      const { data: profiles, error: profileError } = await supabase
        .from('profiles')
        .select('id, email')
        .ilike('email', email.trim());

      if (profileError) {
        console.error('Error finding user:', profileError);
        toast.error(`Failed to find user profile: ${profileError.message}`);
        return;
      }

      if (!profiles || profiles.length === 0) {
        setShowInviteDialog(true);
        return;
      }

      const targetProfile = profiles[0];

      const { data: existingShares, error: sharesError } = await supabase
        .from('project_shares')
        .select('id')
        .eq('project_id', projectId)
        .eq('shared_with', targetProfile.id)
        .limit(1);

      if (sharesError) {
        console.error('Error checking existing shares:', sharesError);
        toast.error(`Failed to check existing shares: ${sharesError.message}`);
        return;
      }

      if (existingShares && existingShares.length > 0) {
        toast.error("Project already shared with this user");
        return;
      }

      const { error: shareError } = await supabase
        .from("project_shares")
        .insert({
          project_id: projectId,
          shared_by: user.id,
          shared_with: targetProfile.id,
          permission,
        });

      if (shareError) {
        console.error('Error sharing project:', shareError);
        toast.error(`Failed to share project: ${shareError.message}`);
        return;
      }

      toast.success("Project shared successfully");
      onShare?.();
      refetchShares();
      setEmail("");
    } catch (error: any) {
      console.error('Unexpected error:', error);
      toast.error(`An error occurred: ${error.message || 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('share.share_project')}</DialogTitle>
            <DialogDescription>
              {t('share.share_project_description')}
            </DialogDescription>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="users">{t('share.share_with_users')}</TabsTrigger>
              <TabsTrigger value="public">{t('share.public_link')}</TabsTrigger>
            </TabsList>
            
            <TabsContent value="users" className="space-y-4 mt-4">
              {shares && shares.length > 0 && (
                <>
                  <div className="space-y-2">
                    <Label>{t('share.currently_shared_with')}</Label>
                    <div className="space-y-2 max-h-[200px] overflow-y-auto">
                      {shares.map((share) => (
                        <Card key={share.id} className="bg-secondary">
                          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle className="text-sm font-medium truncate max-w-[200px]">
                              {share.shared_with_profile?.full_name || share.shared_with_profile?.email || t('share.unnamed_user')}
                            </CardTitle>
                            <div className="flex gap-2">
                              <Select
                                defaultValue={share.permission}
                                onValueChange={(value: SharePermission) => handleUpdatePermission(share.id, value)}
                              >
                                <SelectTrigger className="w-[100px]">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="view">{t('share.view')}</SelectItem>
                                  <SelectItem value="edit">{t('share.edit')}</SelectItem>
                                </SelectContent>
                              </Select>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="ghost" size="icon">
                                    <UserX className="h-4 w-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>{t('share.remove_share')}</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      {t('share.remove_share_description', {
                                        name: share.shared_with_profile?.full_name || share.shared_with_profile?.email || "this user"
                                      })}
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => handleRemoveShare(share.id)}
                                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    >
                                      {t('share.remove')}
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </CardHeader>
                          <CardContent>
                            <div className="text-sm text-muted-foreground truncate">
                              {share.shared_with_profile?.email}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                  <Separator />
                </>
              )}

              {contacts && contacts.length > 0 && (
                <div className="grid gap-2">
                  <Label>{t('share.share_with_contact')}</Label>
                  <Select onValueChange={handleContactSelect}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={t('share.choose_contact')} />
                    </SelectTrigger>
                    <SelectContent>
                      {contacts
                        .filter(contact => contact.email)
                        .map((contact) => (
                          <SelectItem key={contact.id} value={contact.id}>
                            <span className="truncate block">
                              {contact.name} ({contact.email})
                            </span>
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="grid gap-2">
                <Label htmlFor="email">{t('share.or_enter_email')}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="user@example.com"
                  value={email}
                  onChange={handleEmailChange}
                  className={emailError ? "border-red-500" : ""}
                />
                {emailError && (
                  <span className="text-sm text-red-500">{emailError}</span>
                )}
              </div>

              <div className="grid gap-2">
                <Label>{t('share.permissions')}</Label>
                <RadioGroup value={permission} onValueChange={(value) => setPermission(value as SharePermission)} className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="view" id="view" />
                    <Label htmlFor="view">{t('share.view_only')}</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="edit" id="edit" />
                    <Label htmlFor="edit">{t('share.can_edit')}</Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" onClick={() => onOpenChange(false)}>
                  {t('common.cancel')}
                </Button>
                <Button 
                  onClick={handleShare} 
                  disabled={isLoading || !!emailError}
                >
                  {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {t('share.share')}
                </Button>
              </div>
            </TabsContent>
            
            <TabsContent value="public" className="space-y-4 mt-4">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>{t('share.public_share_link')}</Label>
                  <p className="text-sm text-muted-foreground">
                    {t('share.public_share_link_description')}
                  </p>
                </div>
                
                {publicLinkCreated ? (
                  <div className="space-y-4">
                     <div className="flex items-center gap-2">
                       <Input 
                         readOnly 
                         value={`${window.location.origin}/public-project/${publicLinkToken}`} 
                         className="flex-1"
                       />
                       <Button 
                         variant="outline" 
                         size="icon" 
                         onClick={copyPublicLinkToClipboard}
                         title={t('share.copy_link')}
                       >
                         <Copy className="h-4 w-4" />
                       </Button>
                       <Button
                         variant="outline"
                         size="icon"
                         onClick={() => window.open(`/public-project/${publicLinkToken}`, '_blank')}
                         title={t('share.open_link')}
                       >
                         <ExternalLink className="h-4 w-4" />
                       </Button>
                     </div>
                     
                     {/* Guest Editing Toggle */}
                     <div className="flex items-center justify-between p-3 bg-secondary rounded-md">
                       <div>
                         <Label className="text-sm font-medium">Allow guests to edit</Label>
                         <p className="text-xs text-muted-foreground">Let anyone with the link make changes to your project</p>
                       </div>
                       <input
                         type="checkbox"
                         checked={publicShareStatus?.public_share_editable || false}
                         onChange={async (e) => {
                           try {
                             const { error } = await supabase
                               .from('projects')
                               .update({ public_share_editable: e.target.checked })
                               .eq('id', projectId);
                             
                             if (error) {
                               toast.error("Failed to update guest editing permissions");
                               return;
                             }
                             
                             toast.success(e.target.checked ? "Guest editing enabled" : "Guest editing disabled");
                             refetchPublicShare();
                           } catch (error) {
                             console.error('Error updating guest editing:', error);
                             toast.error("An error occurred");
                           }
                         }}
                         className="h-4 w-4"
                       />
                     </div>
                     
                     <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="destructive">{t('share.disable_public_link')}</Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>{t('share.disable_public_link')}</AlertDialogTitle>
                          <AlertDialogDescription>
                            {t('share.disable_public_link_description')}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={handleDisablePublicLink}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            {t('share.disable_link')}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                ) : (
                  <Button 
                    onClick={handleCreatePublicLink}
                    disabled={publicLinkLoading}
                    className="w-full"
                  >
                    {publicLinkLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    <LinkIcon className="mr-2 h-4 w-4" />
                    {t('share.create_public_link')}
                  </Button>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('share.user_not_found')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('share.user_not_found_description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowInviteDialog(false)}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleInviteUser} className="bg-primary">
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('share.send_invitation')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
